import { type Component, createResource, For, Show } from 'solid-js'
import Avatar from '~/components/Avatar'
import Button from '~/components/Button'
import Chip from '~/components/Chip'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import Skeleton from '~/components/Skeleton'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { type Path, useNavigate } from '~/router'
import { type CustomerDocumentType, customerService } from '~/services/customer.service'
import { quoteService } from '~/services/quote.service'
import { resolveError } from '~/utils/error'
import { customerStateColors, customerStateLabels, formatMoney } from '~/utils/settings'
import { formatBytes } from '~/utils/status'

interface CrmCustomerDrawerPropsType {
  /** null = cerrado. Al cambiar, recarga los datos del cliente. */
  customerId: number | null
  onClose: () => void
  /** Se llama tras cualquier cambio que deba refrescar el tablero. */
  onChanged?: () => void
}

/**
 * Detalle del cliente sin salir del tablero: datos de contacto, sus cotizaciones,
 * documentos y notas.
 *
 * Todo lo que muestra ya existía: `customerService.get` devuelve documentos con su
 * `url` y las notas, y las cotizaciones salen de su propio servicio filtrando por
 * cliente. Aquí sólo se componen.
 */
const CrmCustomerDrawer: Component<CrmCustomerDrawerPropsType> = props => {
  const toast = useToast()
  const navigate = useNavigate()
  const { settings } = useCompanySettings()

  const money = (value: number | string) => formatMoney(Number(value) || 0, settings()?.currency ?? 'COP', settings()?.currencies ?? [])

  const estadoLabel = () => customerStateLabels(settings()?.customerStates ?? [])
  const estadoColor = () => customerStateColors(settings()?.customerStates ?? [])

  const [cliente] = createResource(
    () => props.customerId,
    async id => {
      try {
        return await customerService.get(id)
      } catch {
        return null
      }
    }
  )

  const [cotizaciones] = createResource(
    () => props.customerId,
    async id => {
      try {
        const result = await quoteService.list({ customerId: id, limit: 10 })
        return result.items
      } catch {
        return []
      }
    }
  )

  /** El endpoint de descarga exige sesión: se trae el archivo y se abre como blob. */
  const abrirDocumento = async (doc: CustomerDocumentType) => {
    try {
      const url = await customerService.fetchDocumentBlobUrl(doc)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  return (
    <Drawer isOpen={props.customerId !== null} onClose={props.onClose} position="right" size="md" title="Detalle del cliente">
      <Show
        when={!cliente.loading}
        fallback={
          <div class="p-4 space-y-3">
            <Skeleton variant="line" />
            <Skeleton variant="card" />
          </div>
        }
      >
        <Show when={cliente()} fallback={<p class="p-4 text-foreground-subtle">No se pudo cargar el cliente.</p>}>
          {data => (
            <div class="p-4 space-y-5">
              {/* Cabecera */}
              <div class="flex items-start gap-3">
                <Avatar alt={data().name} size={44} class="rounded-full shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-bold text-foreground truncate">{data().name}</p>
                  <Show when={data().nombreComercial || data().razonSocial}>
                    <p class="text-sm text-foreground-subtle truncate">{data().nombreComercial || data().razonSocial}</p>
                  </Show>
                  <Chip color={estadoColor()[data().estadoCliente] ?? 'default'} variant="soft" size="xs" class="mt-1">
                    {estadoLabel()[data().estadoCliente] ?? data().estadoCliente}
                  </Chip>
                </div>
              </div>

              {/* Contacto */}
              <section>
                <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle mb-2">Información del cliente</h3>
                <dl class="space-y-1.5 text-sm">
                  <div class="flex justify-between gap-3">
                    <dt class="text-foreground-subtle shrink-0">Teléfono</dt>
                    <dd class="text-foreground text-right truncate">{data().phone || data().celular || '—'}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-foreground-subtle shrink-0">Correo</dt>
                    <dd class="text-foreground text-right truncate">{data().email || '—'}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="text-foreground-subtle shrink-0">Origen</dt>
                    <dd class="text-foreground text-right truncate">{data().origenCliente || '—'}</dd>
                  </div>
                </dl>
              </section>

              {/* Cotizaciones */}
              <section>
                <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle mb-2">Cotizaciones</h3>
                <Show when={(cotizaciones() ?? []).length > 0} fallback={<p class="text-sm text-foreground-subtle/70 italic">Sin cotizaciones.</p>}>
                  <div class="space-y-2">
                    <For each={cotizaciones()}>
                      {q => (
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/cotizaciones/${q.id}` as Path)}
                          class="w-full flex items-center justify-between gap-3 rounded-lg border border-border bg-surface p-2.5 text-left hover:border-primary-500/50"
                        >
                          <div class="min-w-0">
                            <p class="text-sm font-medium text-foreground truncate">{q.title || q.number}</p>
                            <p class="text-xs text-foreground-subtle">{q.number}</p>
                          </div>
                          <span class="text-sm font-semibold text-foreground shrink-0">{money(q.total)}</span>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </section>

              {/* Documentos: reutiliza los que ya se suben desde la ficha */}
              <section>
                <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle mb-2">Documentos adjuntos</h3>
                <Show when={(data().documents ?? []).length > 0} fallback={<p class="text-sm text-foreground-subtle/70 italic">Sin documentos.</p>}>
                  <div class="space-y-2">
                    <For each={data().documents}>
                      {doc => (
                        <button
                          type="button"
                          onClick={() => abrirDocumento(doc)}
                          class="w-full flex items-center gap-2.5 rounded-lg border border-border bg-surface p-2.5 text-left hover:border-primary-500/50"
                        >
                          <span class="size-8 shrink-0 rounded-lg bg-danger-soft text-danger flex items-center justify-center">
                            <Icon icon="material-symbols-light:description-outline" size={18} />
                          </span>
                          <div class="min-w-0 flex-1">
                            <p class="text-sm text-foreground truncate">{doc.name}</p>
                            <p class="text-xs text-foreground-subtle">{formatBytes(doc.size)}</p>
                          </div>
                          <Icon icon="material-symbols-light:download" size={18} class="text-foreground-subtle shrink-0" />
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </section>

              {/* Notas internas */}
              <section>
                <h3 class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle mb-2">Notas internas</h3>
                <Show when={(data().notes ?? []).length > 0} fallback={<p class="text-sm text-foreground-subtle/70 italic">Sin notas.</p>}>
                  <div class="space-y-2">
                    <For each={data().notes}>
                      {nota => (
                        <div class="rounded-lg bg-surface-muted/50 p-2.5">
                          <p class="text-sm text-foreground">{nota.note}</p>
                          <p class="text-xs text-foreground-subtle mt-1">{new Date(nota.createdAt).toLocaleDateString('es-CO')}</p>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </section>

              <Button class="w-full" variant="soft" color="primary" prependIcon="material-symbols-light:open-in-new" onClick={() => navigate(`/admin/clientes/${data().id}` as Path)}>
                Abrir ficha completa
              </Button>
            </div>
          )}
        </Show>
      </Show>
    </Drawer>
  )
}

export default CrmCustomerDrawer
